import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Tabs, TabsContent, TabsList, TabsTrigger 
} from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";

import { FlavorManager } from "@/components/management/FlavorManager";
import { AttributeManager } from "@/components/management/AttributeManager";
import { BrandManager } from "@/components/management/BrandManager";
import { DrinkManager } from "@/components/management/DrinkManager";
import { DataManager } from "@/components/management/DataManager";
import { UserManager } from "@/components/management/UserManager";
import { InitializePermissions } from "@/components/management/InitializePermissions";
import { LocationManager } from "@/components/management/LocationManager";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

const ManagementPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("flavors");
  const { hasPermission, isLoading: userLoading } = usePermissions();
  
  const isAdmin = hasPermission('admin');
  const canManageUsers = hasPermission('manage_users') || isAdmin;
  const canManageFlavors = hasPermission('manage_flavors') || isAdmin;
  const canManageAttributes = hasPermission('manage_attributes') || isAdmin;
  const canViewManager = hasPermission('manager_view') || isAdmin;
  
  const hasAnyManagementPermission = 
    isAdmin || 
    canManageUsers || 
    canManageFlavors || 
    canManageAttributes ||  
    canViewManager; 
    
  const canSeeUsersTab = canManageUsers || isAdmin;
  
  useEffect(() => {
    if (!userLoading && !hasAnyManagementPermission) {
      console.log("User does not have management permissions, redirecting...");
      navigate('/');
    }
  }, [userLoading, hasAnyManagementPermission, navigate]);
  
  useEffect(() => {
    if (!canSeeUsersTab && activeTab === 'users') {
      setActiveTab('flavors');
    }
    if (!isAdmin && activeTab === 'reset') {
      setActiveTab('flavors');
    }
  }, [canSeeUsersTab, isAdmin, activeTab]);

  const visibleTabs = [
    'flavors', 
    'attributes', 
    'brands', 
    'drinks', 
    'locations',
    canSeeUsersTab ? 'users' : null,
    isAdmin ? 'reset' : null
  ].filter(Boolean);
  
  if (userLoading || !hasAnyManagementPermission) {
    return null;
  }
  
  return (
    <div className="h-full flex flex-col p-6 mx-auto w-full min-w-[1100px] overflow-x-auto">
      <Toaster />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden min-h-0">
        {visibleTabs.length > 0 && (
           <TabsList className="flex w-full items-center justify-start border-b">
            <TabsTrigger value="flavors">Flavors</TabsTrigger>
            <TabsTrigger value="attributes">Attributes</TabsTrigger>
            <TabsTrigger value="brands">Brands</TabsTrigger>
            <TabsTrigger value="drinks">Drinks</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
            {canSeeUsersTab && <TabsTrigger value="users">Users</TabsTrigger>}
            {isAdmin && <TabsTrigger value="reset">Data Reset</TabsTrigger>}
          </TabsList>
        )}
        
        <TabsContent value="flavors" className="flex-1 mt-6 flex flex-col overflow-hidden min-h-0">
          <FlavorManager />
        </TabsContent>
        
        <TabsContent value="attributes" className="flex-1 mt-6 flex flex-col overflow-hidden min-h-0">
          <AttributeManager />
        </TabsContent>
        
        <TabsContent value="brands" className="flex-1 mt-6 flex flex-col overflow-hidden min-h-0">
          <BrandManager />
        </TabsContent>
        
        <TabsContent value="drinks" className="flex-1 mt-6 flex flex-col overflow-hidden min-h-0">
          <DrinkManager />
        </TabsContent>

        <TabsContent value="locations" className="flex-1 mt-6 flex flex-col overflow-hidden min-h-0">
          <LocationManager />
        </TabsContent>

        {canSeeUsersTab && (
          <TabsContent value="users" className="flex-1 mt-6 flex flex-col overflow-auto min-h-0">
            <div className="space-y-6">
              <PermissionGuard requiredPermission="admin">
                <InitializePermissions />
              </PermissionGuard>
              <UserManager />
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="reset" className="flex-1 mt-6 flex flex-col overflow-hidden min-h-0">
            <DataManager />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ManagementPage; 